import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Group, Modal, ModalProps, Radio, Stack} from "@mantine/core";
import {useCallback} from "react";
import {
  CompanyEntity,
  getObjectId,
  PersonEntity,
  SiteEntity,
  TrailerEntity,
  VehicleEntity
} from "@ots-frontend/ots-data";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {PersonSelect} from "../../select/entity/PersonSelect/PersonSelect";
import {VehicleSelect} from "../../select/entity/VehicleSelect/VehicleSelect";
import {TrailerSelect} from "../../select/entity/TrailerSelect/TrailerSelect";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {SiteSelect} from "../../select/entity/SiteSelect/SiteSelect";

interface TicketFilterFormProps {
  defaultValues?: Partial<TicketFilterFormValues>;
  onSubmit?: (values: TicketFilterFormValues) => void;
}

export interface TicketFilterFormValues {
  person?: PersonEntity | string;
  vehicle?: VehicleEntity | string;
  trailer?: TrailerEntity | string;
  pickupSite?: SiteEntity | string;
  pickupSiteCompany?: CompanyEntity | string;
  deliverySite?: SiteEntity | string;
  deliverySiteCompany?: CompanyEntity | string;
  adminValidated?: boolean
  dispatchValidated?: boolean
  fieldValidated?: boolean
  businessValidated?: boolean
}

export const DEFAULT_TICKET_FILTER: TicketFilterFormValues = {
  person: undefined,
  vehicle: undefined,
  trailer: undefined,
  pickupSite: undefined,
  pickupSiteCompany: undefined,
  deliverySite: undefined,
  deliverySiteCompany: undefined,
  adminValidated: undefined,
  dispatchValidated: undefined,
  fieldValidated: undefined,
  businessValidated: undefined,
}

export const isDefaultTicketFilter = (filter1: TicketFilterFormValues): boolean => {
  if (filter1 === DEFAULT_TICKET_FILTER) {
    return true
  }

  if (getObjectId(filter1.person) !== getObjectId(DEFAULT_TICKET_FILTER.person)) {
    return false;
  }

  if (getObjectId(filter1.vehicle) !== getObjectId(DEFAULT_TICKET_FILTER.vehicle)) {
    return false;
  }

  if (getObjectId(filter1.trailer) !== getObjectId(DEFAULT_TICKET_FILTER.trailer)) {
    return false;
  }

  if (getObjectId(filter1.pickupSite) !== getObjectId(DEFAULT_TICKET_FILTER.pickupSite)) {
    return false;
  }

  if (getObjectId(filter1.pickupSiteCompany) !== getObjectId(DEFAULT_TICKET_FILTER.pickupSiteCompany)) {
    return false;
  }

  if (getObjectId(filter1.deliverySite) !== getObjectId(DEFAULT_TICKET_FILTER.deliverySite)) {
    return false;
  }

  if (getObjectId(filter1.deliverySiteCompany) !== getObjectId(DEFAULT_TICKET_FILTER.deliverySiteCompany)) {
    return false;
  }

  if (filter1.adminValidated !== DEFAULT_TICKET_FILTER.adminValidated) {
    return false;
  }

  if (filter1.dispatchValidated !== DEFAULT_TICKET_FILTER.dispatchValidated) {
    return false;
  }

  if (filter1.fieldValidated !== DEFAULT_TICKET_FILTER.fieldValidated) {
    return false;
  }

  if (filter1.businessValidated !== DEFAULT_TICKET_FILTER.businessValidated) {
    return false;
  }

  return true;
}

export const TicketFilterForm = (props: TicketFilterFormProps) => {
  const {
    defaultValues,
    onSubmit,
  } = props;

  const {t} = useTranslation(["common"]);

  const {regionId} = useApp()

  const form = useForm({
    initialValues: {
      person: getObjectId(defaultValues?.person),
      vehicle: getObjectId(defaultValues?.vehicle),
      trailer: getObjectId(defaultValues?.trailer),
      pickupSite: getObjectId(defaultValues?.pickupSite),
      pickupSiteCompany: getObjectId(defaultValues?.pickupSiteCompany),
      deliverySite: getObjectId(defaultValues?.deliverySite),
      deliverySiteCompany: getObjectId(defaultValues?.deliverySiteCompany),
      adminValidated: typeof defaultValues?.adminValidated === "undefined" ? "none" : (defaultValues?.adminValidated ? "validated" : "notValidated"),
      dispatchValidated: typeof defaultValues?.dispatchValidated === "undefined" ? "none" : (defaultValues?.dispatchValidated ? "validated" : "notValidated"),
      fieldValidated: typeof defaultValues?.fieldValidated === "undefined" ? "none" : (defaultValues?.fieldValidated ? "validated" : "notValidated"),
      businessValidated: typeof defaultValues?.businessValidated === "undefined" ? "none" : (defaultValues?.businessValidated ? "validated" : "notValidated"),
    },
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        person: values.person,
        vehicle: values.vehicle,
        trailer: values.trailer,
        pickupSite: values.pickupSite,
        pickupSiteCompany: values.pickupSiteCompany,
        deliverySite: values.deliverySite,
        deliverySiteCompany: values.deliverySiteCompany,
        adminValidated: values.adminValidated === "none" ? undefined : (values.adminValidated === "validated"),
        dispatchValidated: values.dispatchValidated === "none" ? undefined : (values.dispatchValidated === "validated"),
        fieldValidated: values.fieldValidated === "none" ? undefined : (values.fieldValidated === "validated"),
        businessValidated: values.businessValidated === "none" ? undefined : (values.businessValidated === "validated"),
      });
    }
  }, [form, onSubmit]);

  const onClearClick = useCallback(() => {
    if (onSubmit) {
      onSubmit(DEFAULT_TICKET_FILTER)
    }
  }, [onSubmit])

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Stack spacing="md">
        <PersonSelect
          label={t("common.entity.tta.filter.form.field.person", "User")}
          placeholder={t<string>("common.entity.tta.filter.form.field.person", "User")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("person")} />
        <VehicleSelect
          label={t("common.entity.tta.filter.form.field.vehicle", "Truck")}
          placeholder={t<string>("common.entity.tta.filter.form.field.vehicle", "Truck")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("vehicle")} />
        <TrailerSelect
          label={t("common.entity.tta.filter.form.field.trailer", "Trailer")}
          placeholder={t<string>("common.entity.tta.filter.form.field.trailer", "Trailer")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("trailer")} />
        <Divider/>
        <CompanySelect
          label={t("common.entity.dispatch.filter.form.field.pickupcompany", "Pickup Company")}
          placeholder={t<string>("common.entity.dispatch.filter.form.field.pickupcompany", "Pickup Company")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("pickupSiteCompany")} />
        <SiteSelect
          label={t("common.entity.tta.filter.form.field.pickupsite", "Pickup Site")}
          placeholder={t<string>("common.entity.tta.filter.form.field.pickupsite", "Pickup Site")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("pickupSite")} />
        <Divider/>
        <CompanySelect
          label={t("common.entity.tta.filter.form.field.deliverycompany", "Delivery Company")}
          placeholder={t<string>("common.entity.tta.filter.form.field.deliverycompany", "Delivery Company")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("deliverySiteCompany")} />
        <SiteSelect
          label={t("common.entity.tta.filter.form.field.deliverysite", "Delivery Site")}
          placeholder={t<string>("common.entity.tta.filter.form.field.deliverysite", "Delivery Site")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("deliverySite")} />
        <Divider my="sm" variant="dotted"/>
        <Radio.Group
          label={t("common.entity.tta.filter.form.field.adminvalidated", "Admin Validated")}
          {...form.getInputProps("adminValidated")}
        >
          <Group mt="xs">
            <Radio value="none" label="None"/>
            <Radio value="validated" label="Validated"/>
            <Radio value="notValidated" label="Not Validated"/>
          </Group>
        </Radio.Group>
        <Radio.Group
          label={t("common.entity.tta.filter.form.field.dispatchvalidated", "Dispatch Validated")}
          {...form.getInputProps("dispatchValidated")}
        >
          <Group mt="xs">
            <Radio value="none" label="None"/>
            <Radio value="validated" label="Validated"/>
            <Radio value="notValidated" label="Not Validated"/>
          </Group>
        </Radio.Group>
        <Radio.Group
          label={t("common.entity.tta.filter.form.field.fieldvalidated", "Field Validated")}
          {...form.getInputProps("fieldValidated")}
        >
          <Group mt="xs">
            <Radio value="none" label="None"/>
            <Radio value="validated" label="Validated"/>
            <Radio value="notValidated" label="Not Validated"/>
          </Group>
        </Radio.Group>
        <Radio.Group
          label={t("common.entity.tta.filter.form.field.businessvalidated", "Business Validated")}
          {...form.getInputProps("businessValidated")}
        >
          <Group mt="xs">
            <Radio value="none" label="None"/>
            <Radio value="validated" label="Validated"/>
            <Radio value="notValidated" label="Not Validated"/>
          </Group>
        </Radio.Group>
      </Stack>
      <Group position="center" grow={true}>
        <Button fullWidth mt="xl" onClick={onClearClick}>
          {t("common.action.clear", "Clear", {ns: "common"})}
        </Button>
        <Button type="submit" fullWidth mt="xl">
          {t("common.action.submit", "Submit", {ns: "common"})}
        </Button>
      </Group>
    </form>
  );
};

interface TicketFilterFormModalProps extends TicketFilterFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const TicketFilterFormModal = (props: TicketFilterFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <TicketFilterForm defaultValues={defaultValues} onSubmit={onSubmit}/>
    </Modal>
  );
};
